/**
 * Reusable detail-screen actions menu.
 *
 * Renders a dropdown trigger (defaults to "More") with the standard
 * V2 detail-screen actions: hide / unhide / delete. Each is wired by
 * the parent — this component owns no mutation state, just the UI.
 */
import { ReactNode } from "react";
import { EyeOff, Eye, MoreHorizontal, Trash2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Props = {
  isHidden: boolean;
  onToggleHide: () => void;
  onDelete: () => void;
  /** Optional extra items rendered above the destructive action. */
  extra?: ReactNode;
  /** Override the trigger button copy. */
  triggerLabel?: string;
  disabled?: boolean;
};

export function DetailActionsMenu({
  isHidden,
  onToggleHide,
  onDelete,
  extra,
  triggerLabel,
  disabled,
}: Props) {
  const { t } = useLanguage();
  const trigger = triggerLabel ?? t("admin.detail.more", "More");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <MoreHorizontal className="mr-1.5 h-4 w-4" />
          {trigger}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {extra}
        <DropdownMenuItem onClick={onToggleHide}>
          {isHidden ? (
            <>
              <Eye className="mr-2 h-4 w-4" />
              {t("admin.detail.unhide", "Unhide")}
            </>
          ) : (
            <>
              <EyeOff className="mr-2 h-4 w-4" />
              {t("admin.detail.hideListing", "Hide from listing")}
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onDelete}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t("admin.detail.delete", "Delete…")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
